<script lang="ts" setup>
import { ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { VForm } from "vuetify/components";

import OmniDialog from "@/components/omni/OmniDialog.vue";
import OrderPaymentItem from "@/components/order/OrderPayment.vue";
import { useOrderPayments } from "@/composables";

import type { OrderPayment } from "@/api/orders/dto/order.dto";
import OrderPaymentStatus from "@/components/statuses/OrderPaymentStatus.vue";

const { t } = useI18n();
const { isLoading, updateOrderPayment, createOrderPaymentDraft } = useOrderPayments();

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
  orderId: string;
  payment: OrderPayment | null;
}>();

const emit = defineEmits<{
  (e: "success", payment: OrderPayment): void;
}>();

const formRef = useTemplateRef<VForm>("form");
const editedPayment = ref<OrderPayment>(createOrderPaymentDraft());

function close() {
  showDialog.value = false;
}

async function savePayment() {
  const validation = await formRef.value?.validate();

  if (!validation?.valid || !editedPayment.value.id) {
    return;
  }

  const updatedPayment = await updateOrderPayment(props.orderId, editedPayment.value.id, {
    type: editedPayment.value.type,
    rate: editedPayment.value.rate,
  });

  if (!updatedPayment) {
    return;
  }

  emit("success", updatedPayment);
  close();
}

watch(showDialog, (value) => {
  if (!value || !props.payment) {
    return;
  }

  editedPayment.value = { ...props.payment };
});
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :primary-action-label="t('general.save')"
    :primary-button-loading="isLoading"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isLoading"
    :title="t('payments.edit-payment')"
    width="800"
    @primary-action:click="savePayment"
    @secondary-action:click="close"
  >
    <template #title:append>
      <order-payment-status v-if="editedPayment.status" :status="editedPayment.status" class="ml-4" />
    </template>

    <v-form ref="form">
      <order-payment-item v-model="editedPayment" hide-delete />
    </v-form>
  </omni-dialog>
</template>
